import React from "react";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchAwareness } from "../../api/awareness";
import "../../styles/circuit-background.css";
import {
  faShieldAlt,
  faLock,
  faBug,
  faLaptopCode,
  faNetworkWired,
  faKey,
  faFish,
  faUsers,
  faServer,
  faDatabase,
  faExclamationTriangle,
  faInfoCircle,
  faArrowRight,
  faUserShield,
  faHome,
  faChild,
  faMobileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";

const SecurityCard = ({ icon, title, description, bulletPoints, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{
      y: -8,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }}
    className="bg-white p-8 rounded-xl shadow-lg transition-all duration-300 border-b-4 border-primary overflow-hidden group relative"
  >
    <div className="absolute -right-10 -top-10 w-20 h-20 bg-gradient-to-br from-primary/30 to-primary/5 rounded-full"></div>

    <div className="flex items-start relative z-10">
      <div className="bg-primary p-4 rounded-lg text-white transform transition-transform group-hover:rotate-6 group-hover:scale-110 flex-shrink-0">
        <FontAwesomeIcon icon={icon} className="text-2xl" />
      </div>

      <div className="ml-5">
        <h3 className="text-h-6 font-bold text-heading mb-3 group-hover:text-primary transition-colors font-nunito-sans">
          {title}
        </h3>
        <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
          {description}
        </p>

        <ul className="mt-4 space-y-2">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex items-center text-accent">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
              <span className="text-body font-nunito-sans text-paragraph">{point}</span>
            </li>
          ))}
        </ul>

        <motion.div
          initial={{ opacity: 0, width: 0 }}
          whileHover={{ opacity: 1, width: "100%" }}
          className="h-0.5 bg-primary/20 mt-4"
        ></motion.div>
      </div>
    </div>
  </motion.div>
);

const FamilyTraining = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const threatIcons = {
    "Phishing Scams": faFish,
    "Social Media Risks": faUsers,
    "Smart Device Hacks": faHome,
    "Kids' App Dangers": faChild,
    "Fake Shopping Sites": faLaptopCode,
    "Wi-Fi Eavesdropping": faNetworkWired,
    "Rogue Downloads": faBug,
    "Identity Theft": faUserShield,
  };

  const { data: threats = [], isLoading, isError, error } = useQuery({
    queryKey: ["threats", "Family"],
    queryFn: () => fetchAwareness({ lang: "en", group: "Family" }),
  });

  const familySecurityMeasures = [
    {
      icon: faHome,
      title: t("awareness.family.network.title", "Secure Home Network"),
      description: t(
        "awareness.family.network.description",
        "Keep your family's digital life safe by securing your Wi-Fi and smart devices."
      ),
      bulletPoints: [
        t("awareness.family.network.bulletPoints.password", "Change default router passwords to strong, unique ones"),
        t("awareness.family.network.bulletPoints.encryption", "Enable WPA3 encryption on your Wi-Fi network"),
        t("awareness.family.network.bulletPoints.firmware", "Regularly update firmware on routers and IoT devices"),
      ],
    },
    {
      icon: faChild,
      title: t("awareness.family.kids.title", "Kids' Online Safety"),
      description: t(
        "awareness.family.kids.description",
        "Protect your children from online threats with parental controls and education."
      ),
      bulletPoints: [
        t("awareness.family.kids.bulletPoints.controls", "Set up parental control apps like Qustodio or Net Nanny"),
        t("awareness.family.kids.bulletPoints.personal", "Teach kids to avoid sharing personal info online"),
        t("awareness.family.kids.bulletPoints.monitor", "Monitor their social media and gaming interactions"),
      ],
    },
    {
      icon: faMobileAlt,
      title: t("awareness.family.mobile.title", "Safe Mobile Use"),
      description: t(
        "awareness.family.mobile.description",
        "Ensure family smartphones and tablets are protected from cyber risks."
      ),
      bulletPoints: [
        t("awareness.family.mobile.bulletPoints.antivirus", "Install trusted antivirus apps on all devices"),
        t("awareness.family.mobile.bulletPoints.locks", "Use screen locks and biometric authentication"),
        t("awareness.family.mobile.bulletPoints.vpn", "Avoid public Wi-Fi without a VPN for sensitive tasks"),
      ],
    },
    {
      icon: faShieldAlt,
      title: t("awareness.family.backup.title", "Family Backup Plan"),
      description: t(
        "awareness.family.backup.description",
        "Safeguard precious family memories and data with regular backups."
      ),
      bulletPoints: [
        t("awareness.family.backup.bulletPoints.cloud", "Back up photos and documents to a secure cloud like Google Drive"),
        t("awareness.family.backup.bulletPoints.drive", "Use an external hard drive stored in a safe place"),
        t("awareness.family.backup.bulletPoints.test", "Test restores to ensure backups work when needed"),
      ],
    },
  ];

  const familyCyberRisks = [
    {
      icon: faFish,
      title: t("awareness.family.phishing.title", "Family Phishing Scams"),
      description: t(
        "awareness.family.phishing.description",
        "Cybercriminals may target your family with fake emails or messages."
      ),
      tips: [
        t("awareness.family.phishing.tips.tip1", "Teach everyone to check sender email addresses carefully"),
        t("awareness.family.phishing.tips.tip2", "Avoid downloading attachments from unknown sources"),
        t("awareness.family.phishing.tips.tip3", "Discuss phishing examples as a family activity"),
      ],
    },
    {
      icon: faUsers,
      title: t("awareness.family.social.title", "Social Media Risks"),
      description: t(
        "awareness.family.social.description",
        "Oversharing online can expose your family to privacy threats."
      ),
      tips: [
        t("awareness.family.social.tips.tip1", "Set social media profiles to private"),
        t("awareness.family.social.tips.tip2", "Avoid posting vacation plans in real-time"),
        t("awareness.family.social.tips.tip3", "Educate teens about online stranger danger"),
      ],
    },
  ];

  const introductionSection = [
    {
      title: t("awareness.family.main.title", "Cybersecurity for Families"),
      description: t(
        "awareness.family.main.description",
        "In today’s connected world, families use technology for everything from homework to entertainment. Learn how to protect your household from cyber threats with simple, effective strategies."
      ),
    },
  ];

  const riskSection = [
    {
      title: t("awareness.family.risks.title", "Family Cyber Threats"),
      description: t(
        "awareness.family.risks.description",
        "Discover the common risks families face online and how to stay one step ahead of cybercriminals."
      ),
    },
  ];

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("awareness.family.title", "Family Cybersecurity Training")}
          description={t(
            "awareness.family.subtitle",
            "Equipping families with the tools and knowledge to stay safe online together"
          )}
          path={[
            { label: t("header.home", "Home"), link: "/" },
            { label: t("header.family", "Family"), link: null },
          ]}
          backgroundIcons={[faHome, faChild, faMobileAlt, faShieldAlt, faLock, faKey]}
        />
        <FullscreenSpinner className="py-12" />
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t("awareness.family.title", "Family Cybersecurity Training")}
        description={t(
          "awareness.family.subtitle",
          "Equipping families with the tools and knowledge to stay safe online together"
        )}
        path={[
          { label: t("header.home", "Home"), link: "/" },
          { label: t("header.family", "Family"), link: null },
        ]}
        backgroundIcons={[faHome, faChild, faMobileAlt, faShieldAlt, faLock, faKey]}
      />

      <div className="circuit-page-background">
        <div className="max-xl:px-4 py-8 md:py-16 px-dynamic flex flex-col md:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {introductionSection.map((introduction, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-l-4 border-primary items-start bg-white pb-4 pt-1 pr-8 mb-4 mt-4 rounded-md transition-all duration-300 overflow-hidden group relative"
              >
                <div className="flex items-start relative z-10">
                  <div className="ml-5">
                    <h3 className="text-h-3 lg:text-3xl font-bold text-heading mb-3 transition-colors font-nunito-sans">
                      {introduction.title}
                    </h3>
                    <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
                      {introduction.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Security Measures Grid */}
            <div className="grid lg:grid-cols-2 gap-6 mb-12">
              {familySecurityMeasures.map((measure, index) => (
                <SecurityCard
                  key={index}
                  icon={measure.icon}
                  title={measure.title}
                  description={measure.description}
                  bulletPoints={measure.bulletPoints}
                  delay={index * 0.1}
                />
              ))}
            </div>

            {/* Cyber Risks Section */}
            <section className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-light/30 to-blue-50 opacity-70 rounded-3xl -z-10"></div>
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-light/50 rounded-full opacity-30 blur-2xl -z-10"></div>
              <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-blue-100 rounded-full opacity-40 blur-3xl -z-10"></div>

              <div className="relative md:mt-24">
                {riskSection.map((risks, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-l-4 border-primary items-start bg-white pb-8 pt-1 pr-8 mb-8 rounded-md transition-all duration-300 overflow-hidden group relative"
                  >
                    <div className="flex items-start relative z-10">
                      <div className="ml-5">
                        <h3 className="text-h-3 lg:text-3xl font-bold text-heading mb-3 transition-colors font-nunito-sans">
                          {risks.title}
                        </h3>
                        <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
                          {risks.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}

                <div className="grid md:grid-cols-2 gap-10 mb-10">
                  {familyCyberRisks.map((risk, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.2 }}
                      className="relative"
                    >
                      <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 group">
                        <div className="bg-primary p-6 flex items-center relative overflow-hidden">
                          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                          <div className="absolute left-0 bottom-0 w-16 h-16 bg-white/10 rounded-full -ml-5 -mb-5"></div>
                          <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm mr-4">
                            <FontAwesomeIcon icon={risk.icon} className="text-h-6 text-white" />
                          </div>
                          <h3 className="text-h-6 font-bold text-white font-nunito-sans">
                            {risk.title}
                          </h3>
                        </div>
                        <div className="p-6">
                          <p className="text-body-big text-paragraph leading-relaxed font-nunito-sans">
                            {risk.description}
                          </p>
                          <div>
                            <h4 className="font-bold text-gray-800 mb-3 mt-3 flex items-center font-nunito-sans">
                              <FontAwesomeIcon icon={faShieldAlt} className="text-primary mr-2" />
                              {t("awareness.family.risks.safety_measures", "Safety Measures")}
                            </h4>
                            <ul className="space-y-3">
                              {risk.tips.map((tip, idx) => (
                                <motion.li
                                  key={idx}
                                  initial={{ opacity: 0, x: -10 }}
                                  whileInView={{ opacity: 1, x: 0 }}
                                  transition={{ delay: idx * 0.1 }}
                                  viewport={{ once: true }}
                                  className="flex items-start group/tip"
                                >
                                  <div className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center mt-0.5 mr-3 group-hover/tip:bg-primary group-hover/tip:text-white transition-colors">
                                    <span className="text-body font-bold">{idx + 1}</span>
                                  </div>
                                  <span className="text-paragraph text-body group-hover/tip:text-gray-900 transition-colors font-nunito-sans">
                                    {tip}
                                  </span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Awareness Sidebar */}
          <div className="md:w-[30%]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl md:p-8 relative overflow-hidden sticky top-24 max-xl:top-32"
            >
              <div className="absolute top-0 right-0 opacity-10">
                <svg width="300" height="300" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4F46E5" d="M40,0 L60,0 L100,40 L40,100 L0,60 Z" />
                  <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                  <rect x="120" y="100" width="60" height="60" fill="#4F46E5" />
                </svg>
              </div>

              <div className="flex items-start mb-10 relative z-10 border-l-4 border-primary rounded-md">
                <div className="ml-5">
                  <h2 className="lg:text-3xl text-h-3 max-xl:text-2xl font-bold text-heading font-catamaran">
                    {t("awareness.family.awareness.title", "Family Cyber Awareness")}
                  </h2>
                  <p className="text-gray-600 mt-1 font-nunito-sans">
                    {t("awareness.family.awareness.subtitle", "Key threats every family member should know")}
                  </p>
                </div>
              </div>

              <div className="max-md:p-4 grid md:grid-cols-1 gap-6 relative z-10">
                {threats.length > 0 ? (
                  threats.map((item) => (
                    <motion.div
                      key={`${item.group}-${item.threat_type[0]}`}
                      whileHover={{ y: -5 }}
                      className="flex items-center p-4 bg-white rounded-xl shadow-md transition-all duration-300 cursor-pointer"
                      onClick={() => navigate(`/awareness-detail/${item.group}/${item.threat_type[0]}`)}
                    >
                      <FontAwesomeIcon
                        icon={threatIcons[item.threat_type[0]] || faBug}
                        className="text-primary text-xl mr-3"
                      />
                      <span className="text-body text-paragraph font-nunito-sans">{item.threat_type[0]}</span>
                    </motion.div>
                  ))
                ) : (
                  <p>No threats available for this group.</p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FamilyTraining;